import React, { useRef, useState, useEffect } from 'react';
import { Input, Button, message, Select, Modal, Switch, Tooltip, Spin, Progress } from 'antd';
import {
  FormatPainterOutlined,
  UploadOutlined,
  DeleteOutlined,
  ScissorOutlined,
  SwapOutlined,
  DeleteFilled,
  QrcodeOutlined,
  CopyOutlined,
  SnippetsOutlined,
  FileTextOutlined,
  DownloadOutlined,
  AimOutlined,
  UndoOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import QRCodeLib from 'qrcode';
import useGlassCard from '../../hooks/useGlassCard';
import { copyToClipboard, readFromClipboard } from '../../utils/clipboard';
import './TextProcessor.css';

const { TextArea } = Input;

const QR_SIZE_CONFIG: Record<string, { size: number; cols: number; label: string }> = {
  small: { size: 120, cols: 6, label: '小' },
  medium: { size: 150, cols: 5, label: '中' },
  large: { size: 200, cols: 4, label: '大' },
};

const QR_DOT_STYLES = [
  { value: 'square', label: '方形' },
  { value: 'dots', label: '小圆点' },
  { value: 'extra-rounded', label: '大圆点' },
  { value: 'rounded', label: '圆角' },
  { value: 'liquid', label: '液化' },
  { value: 'classy', label: '经典' },
  { value: 'diamond', label: '菱形' },
  { value: 'star', label: '星形' },
];

function renderQRCode(
  canvas: HTMLCanvasElement,
  options: { value: string; size: number; dotStyle: string; fgColor?: string; bgColor?: string }
) {
  const { value, size, dotStyle, fgColor = '#1e2f3e', bgColor = '#ffffff' } = options;

  let qrData;
  try {
    qrData = QRCodeLib.create(value, { errorCorrectionLevel: 'M' });
  } catch {
    return;
  }

  const modules = qrData.modules;
  const moduleCount = modules.size;

  const dpr = window.devicePixelRatio || 1;
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;

  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);

  const margin = 4;
  const totalModules = moduleCount + margin * 2;
  const ms = size / totalModules;

  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, size, size);

  const isDark = (r: number, c: number) => {
    if (r < 0 || r >= moduleCount || c < 0 || c >= moduleCount) return false;
    return !!modules.data[r * moduleCount + c];
  };

  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      if (!isDark(row, col)) continue;

      const x = (col + margin) * ms;
      const y = (row + margin) * ms;
      const cx = x + ms / 2;
      const cy = y + ms / 2;

      ctx.fillStyle = fgColor;

      switch (dotStyle) {
        case 'square':
          ctx.fillRect(x, y, ms + 0.5, ms + 0.5);
          break;

        case 'dots':
          ctx.beginPath();
          ctx.arc(cx, cy, ms * 0.32, 0, Math.PI * 2);
          ctx.fill();
          break;

        case 'extra-rounded':
          ctx.beginPath();
          ctx.arc(cx, cy, ms * 0.44, 0, Math.PI * 2);
          ctx.fill();
          break;

        case 'rounded': {
          const r = ms * 0.32;
          const s = ms;
          ctx.beginPath();
          ctx.moveTo(x + r, y);
          ctx.lineTo(x + s - r, y);
          ctx.quadraticCurveTo(x + s, y, x + s, y + r);
          ctx.lineTo(x + s, y + s - r);
          ctx.quadraticCurveTo(x + s, y + s, x + s - r, y + s);
          ctx.lineTo(x + r, y + s);
          ctx.quadraticCurveTo(x, y + s, x, y + s - r);
          ctx.lineTo(x, y + r);
          ctx.quadraticCurveTo(x, y, x + r, y);
          ctx.closePath();
          ctx.fill();
          break;
        }

        case 'diamond': {
          const dr = ms * 0.48;
          ctx.beginPath();
          ctx.moveTo(cx, cy - dr);
          ctx.lineTo(cx + dr, cy);
          ctx.lineTo(cx, cy + dr);
          ctx.lineTo(cx - dr, cy);
          ctx.closePath();
          ctx.fill();
          break;
        }

        case 'liquid': {
          const lr = ms * 0.5;
          ctx.beginPath();
          ctx.arc(cx, cy, lr, 0, Math.PI * 2);
          ctx.fill();
          if (isDark(row, col + 1)) {
            ctx.fillRect(cx, cy - lr, ms, lr * 2);
          }
          if (isDark(row + 1, col)) {
            ctx.fillRect(cx - lr, cy, lr * 2, ms);
          }
          break;
        }

        case 'classy': {
          if ((row + col) % 2 === 0) {
            ctx.beginPath();
            ctx.arc(cx, cy, ms * 0.4, 0, Math.PI * 2);
            ctx.fill();
          } else {
            const pad = ms * 0.12;
            ctx.fillRect(x + pad, y + pad, ms - pad * 2, ms - pad * 2);
          }
          break;
        }

        case 'star': {
          const outerR = ms * 0.46;
          const innerR = ms * 0.2;
          const points = 4;
          ctx.beginPath();
          for (let i = 0; i < points * 2; i++) {
            const radius = i % 2 === 0 ? outerR : innerR;
            const angle = (Math.PI * i) / points - Math.PI / 2;
            const px = cx + radius * Math.cos(angle);
            const py = cy + radius * Math.sin(angle);
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
          ctx.fill();
          break;
        }

        default:
          ctx.fillRect(x, y, ms + 0.5, ms + 0.5);
      }
    }
  }
}

const QR_BATCH_SIZE = 20;
const QR_MAX_COUNT = 500;

const QRCodeItem: React.FC<{
  value: string;
  size: number;
  dotStyle: string;
  index: number;
  onClick?: () => void;
}> = ({ value, size, dotStyle, index, onClick }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !canvasRef.current) return;
    renderQRCode(canvasRef.current, { value, size, dotStyle });
  }, [visible, value, size, dotStyle]);

  return (
    <div className="tp-qr-item tp-qr-item-clickable" ref={containerRef} onClick={onClick}>
      {visible ? (
        <canvas ref={canvasRef} />
      ) : (
        <div style={{ width: size, height: size, background: 'rgba(139,92,246,0.04)', borderRadius: 8 }} />
      )}
      <div className="tp-qr-index">#{index + 1}</div>
      <div className="tp-qr-text" title={value}>
        {value.length > 30 ? value.substring(0, 30) + '...' : value}
      </div>
    </div>
  );
};

const TextProcessor: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const inputCardRef = useRef<HTMLDivElement>(null);
  const outputCardRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [visible, setVisible] = useState(false);

  useGlassCard(inputCardRef);
  useGlassCard(outputCardRef);

  const [splitChar, setSplitChar] = useState('');
  const [splitIndex, setSplitIndex] = useState('1');

  const [replaceMode, setReplaceMode] = useState<'normal' | 'regex'>('normal');
  const [replaceFind, setReplaceFind] = useState('');
  const [replaceWith, setReplaceWith] = useState('');

  const [aroundKeyword, setAroundKeyword] = useState('');
  const [aroundDirection, setAroundDirection] = useState<'before' | 'after'>('after');
  const [aroundReplace, setAroundReplace] = useState('');

  const [dedupeMode, setDedupeMode] = useState<'line' | 'column' | 'marker' | 'count'>('line');
  const [dedupeSplitChar, setDedupeSplitChar] = useState('');
  const [dedupeColIndex, setDedupeColIndex] = useState(1);
  const [dedupeMarkerPrefix, setDedupeMarkerPrefix] = useState('');
  const [dedupeMarkerSuffix, setDedupeMarkerSuffix] = useState('');
  const [dedupeTargetCount, setDedupeTargetCount] = useState(1);
  const [dedupeCountBy, setDedupeCountBy] = useState<'line' | 'column' | 'marker'>('line');
  const [dedupeCountSplitChar, setDedupeCountSplitChar] = useState('');
  const [dedupeCountColIndex, setDedupeCountColIndex] = useState(1);
  const [dedupeCountMarkerPrefix, setDedupeCountMarkerPrefix] = useState('');
  const [dedupeCountMarkerSuffix, setDedupeCountMarkerSuffix] = useState('');

  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrLines, setQrLines] = useState<string[]>([]);
  const [qrSizeMode, setQrSizeMode] = useState<string>('medium');
  const [qrDotStyle, setQrDotStyle] = useState<string>('square');
  const [qrRenderCount, setQrRenderCount] = useState(0);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrPreview, setQrPreview] = useState<{ value: string; index: number } | null>(null);

  const [exportFormatVisible, setExportFormatVisible] = useState(false);
  const [exportPrefix, setExportPrefix] = useState('1----123----');
  const [exportSuffix, setExportSuffix] = useState('----qq');

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!qrModalVisible || !qrLoading || qrLines.length === 0) return;
    if (qrRenderCount >= qrLines.length) {
      setQrLoading(false);
      return;
    }
    const timer = requestAnimationFrame(() => {
      setQrRenderCount((prev) => Math.min(prev + QR_BATCH_SIZE, qrLines.length));
    });
    return () => cancelAnimationFrame(timer);
  }, [qrModalVisible, qrLoading, qrRenderCount, qrLines.length]);

  const handleImportFile = () => {
    fileInputRef.current?.click();
  };

  const handlePasteFromClipboard = async () => {
    try {
      const result = await readFromClipboard();
      if (result.text) {
        setInput(result.text);
        message.success('粘贴成功');
      } else if (result.error) {
        inputRef.current?.focus();
        message.info({ content: '请直接 Ctrl+V 粘贴到输入框', duration: 3 });
      } else {
        message.warning('剪贴板为空');
      }
    } catch {
      inputRef.current?.focus();
      message.info({ content: '请直接 Ctrl+V 粘贴到输入框', duration: 3 });
    }
  };

  const handleFileRead = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const buffer = reader.result as ArrayBuffer;
      const utf8Text = new TextDecoder('utf-8').decode(buffer);
      if (utf8Text.includes('\uFFFD')) {
        const gbkText = new TextDecoder('gbk').decode(buffer);
        setInput(gbkText);
      } else {
        setInput(utf8Text);
      }
      message.success('文件导入成功');
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSplit = () => {
    if (!input.trim()) { message.warning('请输入文本'); return; }
    if (!splitChar) { message.warning('请输入分隔符'); return; }
    if (!splitIndex.trim()) { message.warning('请输入提取列号'); return; }

    const indices = splitIndex.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n > 0);
    if (indices.length === 0) { message.warning('请输入有效的列号'); return; }

    const lines = input.split('\n').filter(Boolean);
    const results = lines.map((line) => {
      const parts = line.split(splitChar);
      return indices.map(idx => parts[idx - 1] !== undefined ? parts[idx - 1] : '').join(splitChar);
    });
    setOutput(results.join('\n'));
    message.success(`分割完成，共处理 ${results.length} 行`);
  };

  const handleReplace = () => {
    if (!input.trim()) { message.warning('请输入文本'); return; }
    if (!replaceFind) { message.warning('请输入查找内容'); return; }

    if (replaceMode === 'regex') {
      try {
        const regex = new RegExp(replaceFind, 'g');
        const lines = input.split('\n');
        let count = 0;
        const results = lines.map((line) => {
          const matches = line.match(regex);
          if (matches) count += matches.length;
          return line.replace(regex, replaceWith);
        });
        setOutput(results.join('\n'));
        message.success(`替换完成，共替换 ${count} 处`);
      } catch {
        message.error('正则表达式语法错误');
      }
    } else {
      const lines = input.split('\n');
      const escaped = replaceFind.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escaped, 'g');
      let count = 0;
      const results = lines.map((line) => {
        const matches = line.match(regex);
        if (matches) count += matches.length;
        return line.replace(regex, replaceWith);
      });
      setOutput(results.join('\n'));
      message.success(`替换完成，共替换 ${count} 处`);
    }
  };

  const handleReplaceAround = () => {
    if (!input.trim()) { message.warning('请输入文本'); return; }
    if (!aroundKeyword) { message.warning('请输入关键字'); return; }
    const lines = input.split('\n');
    let count = 0;
    const results = lines.map((line) => {
      const idx = line.indexOf(aroundKeyword);
      if (idx === -1) return line;
      count++;
      if (aroundDirection === 'before') {
        return aroundReplace + aroundKeyword + line.slice(idx + aroundKeyword.length);
      } else {
        return line.slice(0, idx) + aroundKeyword + aroundReplace;
      }
    });
    setOutput(results.join('\n'));
    message.success(`替换完成，共处理 ${count} 行`);
  };

  const handleDedupe = () => {
    if (!input.trim()) { message.warning('请输入文本'); return; }
    const lines = input.split('\n').filter(Boolean);
    let result: string[];

    if (dedupeMode === 'line') {
      const seen = new Set<string>();
      result = lines.filter((line) => {
        if (seen.has(line)) return false;
        seen.add(line);
        return true;
      });
    } else if (dedupeMode === 'column') {
      const seen = new Set<string>();
      result = lines.filter((line) => {
        const parts = line.split(dedupeSplitChar);
        const key = parts[dedupeColIndex - 1] ?? '';
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    } else if (dedupeMode === 'marker') {
      // marker 模式：根据前缀和后缀提取中间文本作为唯一标识
      if (!dedupeMarkerPrefix) { message.warning('请输入前缀文本'); return; }
      const seen = new Set<string>();
      result = lines.filter((line) => {
        const startIdx = line.indexOf(dedupeMarkerPrefix);
        if (startIdx === -1) return true; // 不包含前缀的行保留
        const afterPrefix = startIdx + dedupeMarkerPrefix.length;
        let key: string;
        if (dedupeMarkerSuffix) {
          const endIdx = line.indexOf(dedupeMarkerSuffix, afterPrefix);
          if (endIdx === -1) return true; // 不包含后缀的行保留
          key = line.substring(afterPrefix, endIdx);
        } else {
          key = line.substring(afterPrefix);
        }
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    } else {
      // count 模式：提取指定重复次数的数据
      const getKey = (line: string): string => {
        if (dedupeCountBy === 'column') {
          const parts = line.split(dedupeCountSplitChar);
          return parts[dedupeCountColIndex - 1] ?? '';
        } else if (dedupeCountBy === 'marker') {
          const startIdx = line.indexOf(dedupeCountMarkerPrefix);
          if (startIdx === -1) return line;
          const afterPrefix = startIdx + dedupeCountMarkerPrefix.length;
          if (dedupeCountMarkerSuffix) {
            const endIdx = line.indexOf(dedupeCountMarkerSuffix, afterPrefix);
            if (endIdx === -1) return line;
            return line.substring(afterPrefix, endIdx);
          }
          return line.substring(afterPrefix);
        }
        return line;
      };
      const countMap = new Map<string, number>();
      const firstLineMap = new Map<string, string>(); // key -> 首次出现的原始行
      for (const line of lines) {
        const key = getKey(line);
        countMap.set(key, (countMap.get(key) ?? 0) + 1);
        if (!firstLineMap.has(key)) firstLineMap.set(key, line);
      }
      const matched: string[] = [];
      countMap.forEach((c, key) => {
        if (c === dedupeTargetCount) {
          const firstLine = firstLineMap.get(key);
          if (firstLine !== undefined) matched.push(firstLine);
        }
      });
      result = matched;
    }

    const removed = lines.length - result.length;
    setOutput(result.join('\n'));
    message.success(`去重完成，删除 ${removed} 行，保留 ${result.length} 行`);
  };

  const handleGenerateQR = () => {
    if (!input.trim()) { message.warning('请输入文本'); return; }
    const lines = input.split('\n').filter((line) => line.trim() !== '');
    if (lines.length === 0) { message.warning('没有有效内容'); return; }
    if (lines.length > QR_MAX_COUNT) {
      message.warning(`数据量过大（${lines.length} 条），最多支持 ${QR_MAX_COUNT} 条，已截取前 ${QR_MAX_COUNT} 条`);
      lines.splice(QR_MAX_COUNT);
    }
    setQrLines(lines);
    setQrRenderCount(0);
    setQrLoading(true);
    setQrModalVisible(true);
  };

  const handleCopy = async () => {
    if (!output) return;
    const ok = await copyToClipboard(output);
    if (ok) message.success('已复制到剪贴板');
    else message.error('复制失败');
  };

  const handleExport = () => {
    if (!output) { message.warning('没有可导出的内容'); return; }
    const content = output.split(/\r?\n/).join('\r\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'text_result.txt';
    a.click();
    URL.revokeObjectURL(url);
    message.success('导出成功');
  };

  const handleExportFormat = () => {
    if (!output) { message.warning('没有可导出的内容'); return; }
    setExportPrefix('1----123----');
    setExportSuffix('----qq');
    setExportFormatVisible(true);
  };

  const handleExportFormatConfirm = () => {
    const lines = output.split(/\r?\n/);
    const formatted = lines.map((line: string) => exportPrefix + line + exportSuffix).join('\r\n');
    const blob = new Blob([formatted], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'text_result.txt';
    a.click();
    URL.revokeObjectURL(url);
    setExportFormatVisible(false);
    message.success('导出成功');
  };

  const toolCards = [
    {
      icon: <ScissorOutlined />,
      iconBg: 'linear-gradient(135deg, #5080ff 0%, #6450ff 100%)',
      iconShadow: '0 6px 16px rgba(80, 128, 255, 0.3)',
      title: '分割提取',
      color: '#5080ff',
      onReset: () => { setSplitChar(''); setSplitIndex('1'); },
      content: (
        <>
          <div className="tp-field-row">
            <div className="tp-field-item">
              <div className="tp-field-label">分隔符</div>
              <Input size="small" value={splitChar} onChange={(e) => setSplitChar(e.target.value)} placeholder="如 , 或 |" className="tp-input" />
            </div>
            <div className="tp-field-item">
              <div className="tp-field-label">提取列号(如 1 或 1,3)</div>
              <Input size="small" value={splitIndex} onChange={(e) => setSplitIndex(e.target.value)} placeholder="如 1 或 1,3" className="tp-input" />
            </div>
          </div>
          <Button block size="small" type="primary" onClick={handleSplit} className="tp-action-btn" style={{ background: 'linear-gradient(135deg, #5080ff 0%, #6450ff 100%)', border: 'none' }}>
            开始分割
          </Button>
        </>
      ),
    },
    {
      icon: <SwapOutlined />,
      iconBg: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
      iconShadow: '0 6px 16px rgba(245, 158, 11, 0.3)',
      title: '批量替换',
      color: '#f59e0b',
      onReset: () => { setReplaceFind(''); setReplaceWith(''); setReplaceMode('normal'); },
      content: (
        <>
          <div className="tp-field-row">
            <div className="tp-field-item">
              <div className="tp-field-label">{replaceMode === 'regex' ? '正则规则' : '查找'}</div>
              <Input size="small" value={replaceFind} onChange={(e) => setReplaceFind(e.target.value)} placeholder={replaceMode === 'regex' ? '如 \\d+ 或 [a-z]+' : '查找内容'} className="tp-input" />
            </div>
            <div className="tp-field-item">
              <div className="tp-field-label">替换为</div>
              <Input size="small" value={replaceWith} onChange={(e) => setReplaceWith(e.target.value)} placeholder="替换内容" className="tp-input" />
            </div>
          </div>
          <Button block size="small" type="primary" onClick={handleReplace} className="tp-action-btn" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)', border: 'none' }}>
            开始替换
          </Button>
        </>
      ),
      extra: (
        <>
          <span style={{ display: 'inline-flex', width: 20, justifyContent: 'center', alignItems: 'center' }}>
            {replaceMode === 'regex' && (
              <Tooltip
                title={
                  <div style={{ lineHeight: '1.8' }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>正则替换规则参考：</div>
                    <div style={{ marginTop: 6 }}>1. 删除"集"之后（含"集"）：<code style={{ color: '#fbbf24' }}>^.*集</code></div>
                    <div style={{ marginTop: 4 }}>删除"集"之前（含"集"）：<code style={{ color: '#fbbf24' }}>集.*?$</code></div>
                    <div style={{ paddingLeft: 16 }}>替换内容框留空即可</div>
                    <div style={{ marginTop: 6 }}>2. 每行首插入：<code style={{ color: '#fbbf24' }}>^</code>，每行尾插入：<code style={{ color: '#fbbf24' }}>$</code></div>
                    <div style={{ paddingLeft: 16 }}>替换内容为需要插入的文本</div>
                  </div>
                }
                placement="top"
                overlayStyle={{ maxWidth: 420 }}
              >
                <QuestionCircleOutlined style={{ fontSize: 14, color: '#fbbf24', cursor: 'pointer' }} />
              </Tooltip>
            )}
          </span>
          <Tooltip title={replaceMode === 'regex' ? '切换为普通替换' : '切换为正则替换'}>
            <Switch
              size="small"
              checked={replaceMode === 'regex'}
              onChange={(checked) => setReplaceMode(checked ? 'regex' : 'normal')}
              checkedChildren="正则"
              unCheckedChildren="普通"
              className="tp-mode-switch"
              style={{ marginLeft: 6 }}
            />
          </Tooltip>
        </>
      ),
    },
    {
      icon: <AimOutlined />,
      iconBg: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      iconShadow: '0 6px 16px rgba(6, 182, 212, 0.3)',
      title: '替换指定前后',
      color: '#06b6d4',
      onReset: () => { setAroundKeyword(''); setAroundDirection('after'); setAroundReplace(''); },
      content: (
        <>
          <div className="tp-field-row">
            <div className="tp-field-item">
              <div className="tp-field-label">关键字</div>
              <Input size="small" value={aroundKeyword} onChange={(e) => setAroundKeyword(e.target.value)} placeholder="输入关键字" className="tp-input" />
            </div>
            <div className="tp-field-item" style={{ flex: '0 0 22%' }}>
              <div className="tp-field-label">方向</div>
              <Select
                size="small"
                value={aroundDirection}
                onChange={setAroundDirection}
                style={{ width: '100%' }}
                className="tp-select"
                popupClassName="tp-select-dropdown"
                options={[
                  { value: 'before', label: '前' },
                  { value: 'after', label: '后' },
                ]}
              />
            </div>
            <div className="tp-field-item">
              <div className="tp-field-label">替换内容</div>
              <Input size="small" value={aroundReplace} onChange={(e) => setAroundReplace(e.target.value)} placeholder="替换为" className="tp-input" />
            </div>
          </div>
          <Button block size="small" type="primary" onClick={handleReplaceAround} className="tp-action-btn" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', border: 'none' }}>
            开始替换
          </Button>
        </>
      ),
    },
    {
      icon: <DeleteFilled />,
      iconBg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      iconShadow: '0 6px 16px rgba(239, 68, 68, 0.3)',
      title: '数据去重',
      color: '#ef4444',
      onReset: () => { setDedupeMode('line'); setDedupeSplitChar(''); setDedupeColIndex(1); setDedupeMarkerPrefix(''); setDedupeMarkerSuffix(''); setDedupeTargetCount(1); setDedupeCountBy('line'); setDedupeCountSplitChar(''); setDedupeCountColIndex(1); setDedupeCountMarkerPrefix(''); setDedupeCountMarkerSuffix(''); },
      content: (
        <>
          {dedupeMode === 'line' ? (
            <div className="tp-field-item" style={{ marginBottom: 6 }}>
              <div className="tp-field-label">模式</div>
              <Select
                size="small"
                value={dedupeMode}
                onChange={setDedupeMode}
                style={{ width: '100%' }}
                className="tp-select"
                popupClassName="tp-select-dropdown"
                options={[
                  { value: 'line', label: '整行去重' },
                  { value: 'column', label: '按列去重' },
                  { value: 'marker', label: '按标识去重' },
                  { value: 'count', label: '按重复次数提取' },
                ]}
              />
            </div>
          ) : dedupeMode === 'column' ? (
            <div className="tp-field-row" style={{ marginBottom: 6 }}>
              <div className="tp-field-item">
                <div className="tp-field-label">模式</div>
                <Select
                  size="small"
                  value={dedupeMode}
                  onChange={setDedupeMode}
                  style={{ width: '100%' }}
                  className="tp-select"
                  popupClassName="tp-select-dropdown"
                  options={[
                    { value: 'line', label: '整行去重' },
                    { value: 'column', label: '按列去重' },
                    { value: 'marker', label: '按标识去重' },
                    { value: 'count', label: '按重复次数提取' },
                  ]}
                />
              </div>
              <div className="tp-field-item">
                <div className="tp-field-label">分隔符</div>
                <Input size="small" value={dedupeSplitChar} onChange={(e) => setDedupeSplitChar(e.target.value)} placeholder="如 , 或 |" className="tp-input" />
              </div>
              <div className="tp-field-item">
                <div className="tp-field-label">列号</div>
                <Input size="small" type="number" min={1} value={dedupeColIndex} onChange={(e) => setDedupeColIndex(Number(e.target.value) || 1)} placeholder="1" className="tp-input" />
              </div>
            </div>
          ) : dedupeMode === 'marker' ? (
            <div className="tp-field-row" style={{ marginBottom: 6 }}>
              <div className="tp-field-item">
                <div className="tp-field-label">模式</div>
                <Select
                  size="small"
                  value={dedupeMode}
                  onChange={setDedupeMode}
                  style={{ width: '100%' }}
                  className="tp-select"
                  popupClassName="tp-select-dropdown"
                  options={[
                    { value: 'line', label: '整行去重' },
                    { value: 'column', label: '按列去重' },
                    { value: 'marker', label: '按标识去重' },
                    { value: 'count', label: '按重复次数提取' },
                  ]}
                />
              </div>
              <div className="tp-field-item">
                <div className="tp-field-label">前缀文本</div>
                <Input size="small" value={dedupeMarkerPrefix} onChange={(e) => setDedupeMarkerPrefix(e.target.value)} placeholder="如 openid=" className="tp-input" />
              </div>
              <div className="tp-field-item">
                <div className="tp-field-label">后缀文本</div>
                <Input size="small" value={dedupeMarkerSuffix} onChange={(e) => setDedupeMarkerSuffix(e.target.value)} placeholder="如 ;" className="tp-input" />
              </div>
            </div>
          ) : (
            <>
              <div className="tp-field-row" style={{ marginBottom: 6 }}>
                <div className="tp-field-item">
                  <div className="tp-field-label">模式</div>
                  <Select
                    size="small"
                    value={dedupeMode}
                    onChange={setDedupeMode}
                    style={{ width: '100%' }}
                    className="tp-select"
                    popupClassName="tp-select-dropdown"
                    options={[
                      { value: 'line', label: '整行去重' },
                      { value: 'column', label: '按列去重' },
                      { value: 'marker', label: '按标识去重' },
                      { value: 'count', label: '按重复次数提取' },
                    ]}
                  />
                </div>
                <div className="tp-field-item">
                  <div className="tp-field-label">重复次数</div>
                  <Input size="small" type="number" min={1} value={dedupeTargetCount} onChange={(e) => setDedupeTargetCount(Number(e.target.value) || 1)} placeholder="2" className="tp-input" />
                </div>
                <div className="tp-field-item">
                  <div className="tp-field-label">对比依据</div>
                  <Select
                    size="small"
                    value={dedupeCountBy}
                    onChange={setDedupeCountBy}
                    style={{ width: '100%' }}
                    className="tp-select"
                    popupClassName="tp-select-dropdown"
                    options={[
                      { value: 'line', label: '整行' },
                      { value: 'column', label: '按列' },
                      { value: 'marker', label: '按标识' },
                    ]}
                  />
                </div>
              </div>
              {dedupeCountBy === 'column' && (
                <div className="tp-field-row" style={{ marginBottom: 6 }}>
                  <div className="tp-field-item">
                    <div className="tp-field-label">分隔符</div>
                    <Input size="small" value={dedupeCountSplitChar} onChange={(e) => setDedupeCountSplitChar(e.target.value)} placeholder="如 , 或 |" className="tp-input" />
                  </div>
                  <div className="tp-field-item">
                    <div className="tp-field-label">列号</div>
                    <Input size="small" type="number" min={1} value={dedupeCountColIndex} onChange={(e) => setDedupeCountColIndex(Number(e.target.value) || 1)} placeholder="1" className="tp-input" />
                  </div>
                </div>
              )}
              {dedupeCountBy === 'marker' && (
                <div className="tp-field-row" style={{ marginBottom: 6 }}>
                  <div className="tp-field-item">
                    <div className="tp-field-label">前缀文本</div>
                    <Input size="small" value={dedupeCountMarkerPrefix} onChange={(e) => setDedupeCountMarkerPrefix(e.target.value)} placeholder="如 openid=" className="tp-input" />
                  </div>
                  <div className="tp-field-item">
                    <div className="tp-field-label">后缀文本</div>
                    <Input size="small" value={dedupeCountMarkerSuffix} onChange={(e) => setDedupeCountMarkerSuffix(e.target.value)} placeholder="如 ;" className="tp-input" />
                  </div>
                </div>
              )}
            </>
          )}
          <Button block size="small" type="primary" onClick={handleDedupe} className="tp-action-btn" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', border: 'none' }}>
            开始去重
          </Button>
        </>
      ),
    },
    {
      icon: <QrcodeOutlined />,
      iconBg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      iconShadow: '0 6px 16px rgba(139, 92, 246, 0.3)',
      title: '二维码生成',
      color: '#8b5cf6',
      onReset: () => { setQrSizeMode('medium'); setQrDotStyle('square'); },
      content: (
        <>
          <div className="tp-field-row">
            <div className="tp-field-item">
              <div className="tp-field-label">大小</div>
              <Select
                size="small"
                value={qrSizeMode}
                onChange={setQrSizeMode}
                style={{ width: '100%' }}
                className="tp-select"
                popupClassName="tp-select-dropdown"
                options={Object.entries(QR_SIZE_CONFIG).map(([key, cfg]) => ({
                  value: key,
                  label: cfg.label,
                }))}
              />
            </div>
            <div className="tp-field-item">
              <div className="tp-field-label">码点</div>
              <Select
                size="small"
                value={qrDotStyle}
                onChange={setQrDotStyle}
                style={{ width: '100%' }}
                className="tp-select"
                popupClassName="tp-select-dropdown"
                options={QR_DOT_STYLES}
              />
            </div>
          </div>
          <Button block size="small" type="primary" onClick={handleGenerateQR} icon={<QrcodeOutlined />} className="tp-action-btn" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', border: 'none' }}>
            生成二维码
          </Button>
        </>
      ),
    },
  ];

  const currentQrConfig = QR_SIZE_CONFIG[qrSizeMode] || QR_SIZE_CONFIG.medium;

  return (
    <div className="tp-page" ref={pageRef}>
      <div className="tp-bg">
        <div className="tp-bg-orb orb-1"></div>
        <div className="tp-bg-orb orb-2"></div>
        <div className="tp-bg-orb orb-3"></div>
      </div>

      <div className={`tp-content ${visible ? 'tp-content-visible' : ''}`}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,.csv,.json,.log,.text"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileRead(file);
            e.target.value = '';
          }}
        />

        <div ref={inputCardRef} className="tp-glass-card tp-input-card">
          <div className="tp-card-header">
            <div className="tp-card-header-left">
              <div className="tp-card-icon" style={{ background: 'linear-gradient(135deg, #4682ff 0%, #6450ff 100%)', boxShadow: '0 8px 20px rgba(70, 130, 255, 0.3)' }}>
                <FormatPainterOutlined style={{ color: '#fff', fontSize: 20 }} />
              </div>
              <h3 className="tp-card-title">文本输入</h3>
            </div>
            <div className="tp-card-header-right">
              <Button size="small" icon={<UploadOutlined />} onClick={handleImportFile} className="tp-header-btn">
                上传文件
              </Button>
              <Button size="small" icon={<SnippetsOutlined />} onClick={handlePasteFromClipboard} className="tp-header-btn">
                粘贴
              </Button>
              <Button size="small" icon={<DeleteOutlined />} onClick={() => setInput('')} className="tp-header-btn tp-header-btn-danger">
                清空
              </Button>
            </div>
          </div>
          <TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="请输入或粘贴需要处理的文本内容，也可点击上方「上传文件」导入..."
            rows={10}
            className="tp-textarea"
            ref={inputRef}
          />
        </div>

        <div className="tp-tool-cards-row" style={{ marginBottom: 16 }}>
          {toolCards.map((card, index) => (
            <div className="tp-tool-card-col" key={index}>
              <div className="tp-tool-card" style={{ '--card-accent': card.color } as React.CSSProperties}>
                <div className="tp-tool-card-header">
                  <div className="tp-tool-card-icon" style={{ background: card.iconBg, boxShadow: card.iconShadow }}>
                    {React.cloneElement(card.icon as React.ReactElement, { style: { color: '#fff', fontSize: 16 } })}
                  </div>
                  <span className="tp-tool-card-title">{card.title}</span>
                  {card.extra}
                  <Tooltip title="重置">
                    <Button
                      type="text"
                      size="small"
                      icon={<UndoOutlined />}
                      className="tp-reset-btn"
                      onClick={card.onReset}
                    />
                  </Tooltip>
                </div>
                <div className="tp-tool-card-body">
                  {card.content}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div ref={outputCardRef} className="tp-glass-card tp-output-card">
          <div className="tp-card-header">
            <div className="tp-card-header-left">
              <div className="tp-card-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)' }}>
                <FileTextOutlined style={{ color: '#fff', fontSize: 20 }} />
              </div>
              <h3 className="tp-card-title">处理结果</h3>
            </div>
            <div className="tp-card-header-right">
              <Button size="small" icon={<CopyOutlined />} onClick={handleCopy} disabled={!output} className="tp-header-btn">
                复制
              </Button>
              <Button size="small" icon={<DownloadOutlined />} onClick={handleExport} disabled={!output} className="tp-header-btn">
                导出
              </Button>
              <Button size="small" icon={<DownloadOutlined />} onClick={handleExportFormat} disabled={!output} className="tp-header-btn">
                导出格式
              </Button>
              <Button size="small" icon={<DeleteOutlined />} onClick={() => setOutput('')} disabled={!output} className="tp-header-btn tp-header-btn-danger">
                清空
              </Button>
            </div>
          </div>
          <TextArea
            value={output}
            onChange={(e) => setOutput(e.target.value)}
            placeholder="处理结果将显示在这里..."
            rows={10}
            className="tp-textarea"
          />
        </div>
      </div>

      <Modal
        title="导出格式"
        open={exportFormatVisible}
        onOk={handleExportFormatConfirm}
        onCancel={() => setExportFormatVisible(false)}
        okText="确认"
        cancelText="取消"
        centered
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ marginBottom: 4 }}>前缀</div>
            <Input value={exportPrefix} onChange={(e) => setExportPrefix(e.target.value)} placeholder="请输入前缀" />
          </div>
          <div>
            <div style={{ marginBottom: 4 }}>后缀</div>
            <Input value={exportSuffix} onChange={(e) => setExportSuffix(e.target.value)} placeholder="请输入后缀" />
          </div>
        </div>
      </Modal>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <QrcodeOutlined style={{ color: '#8b5cf6' }} />
            <span>二维码生成结果</span>
            {qrLines.length > 0 && (
              <span style={{ fontSize: 12, color: '#8e99a4', fontWeight: 400 }}>
                共 {qrLines.length} 个
              </span>
            )}
          </div>
        }
        open={qrModalVisible}
        onCancel={() => { setQrModalVisible(false); setQrLoading(false); }}
        footer={null}
        width={Math.min(1200, window.innerWidth - 48)}
        centered
        className="tp-qr-modal"
      >
        {qrLoading && qrRenderCount < qrLines.length && (
          <div className="tp-qr-loading">
            <Spin size="small" />
            <span>渲染中 {qrRenderCount}/{qrLines.length}</span>
            <Progress
              percent={Math.round((qrRenderCount / qrLines.length) * 100)}
              size="small"
              showInfo={false}
              strokeColor="#8b5cf6"
              style={{ maxWidth: 200 }}
            />
          </div>
        )}
        <div className="tp-qr-grid" style={{ gridTemplateColumns: `repeat(${currentQrConfig.cols}, 1fr)` }}>
          {qrLines.slice(0, qrRenderCount).map((line, idx) => (
            <QRCodeItem
              key={idx}
              value={line}
              size={currentQrConfig.size}
              dotStyle={qrDotStyle}
              index={idx}
              onClick={() => setQrPreview({ value: line, index: idx })}
            />
          ))}
        </div>
      </Modal>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <QrcodeOutlined style={{ color: '#8b5cf6' }} />
            <span>二维码预览 #{qrPreview ? qrPreview.index + 1 : ''}</span>
          </div>
        }
        open={!!qrPreview}
        onCancel={() => setQrPreview(null)}
        footer={null}
        width={400}
        centered
        className="tp-qr-modal tp-qr-preview-modal"
      >
        {qrPreview && (
          <div className="tp-qr-preview-content">
            <QRCodeItem
              value={qrPreview.value}
              size={300}
              dotStyle={qrDotStyle}
              index={qrPreview.index}
            />
            <div className="tp-qr-preview-text">{qrPreview.value}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TextProcessor;
